/**
 * Where the Compliance Rail starts on an EMR that has its own right-edge tab.
 *
 * Stride docks a chat tab on the right edge, vertically centred, which is also
 * where an unplaced rail is centred. Rather than move either one around the
 * other, an unplaced rail starts above Stride's tab. Once a clinician drags
 * the rail, their position wins and none of this applies.
 *
 * Only Stride is opted in; other EMRs are untouched.
 */

/** Hosts whose page has its own right-edge tab. */
const TAB_HOSTS = ['stridethera.com'];

/**
 * The tab's toggle, found by its accessible name, which is steadier than
 * Stride's generated class names. Exact names: the open chat panel has its
 * own buttons ending in "Stride AI" ("Pop out", "Close") up in its header, and
 * they are not the toggle.
 */
const SIDEBAR_TOGGLES = ['button[aria-label="Open Stride AI"]', 'button[aria-label="Hide Stride AI"]'];

export function needsSidebarPlacement(hostname: string): boolean {
  const host = (hostname || '').toLowerCase();
  return TAB_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * Where the tab starts from the top of the window, so the rail can sit above
 * it. The toggle's own strip is a full-height bare edge, so this is what it
 * renders (the tab). Null when the page has no such tab (yet).
 */
export function sidebarTabTop(doc: Document): number | null {
  const view = doc.defaultView;
  const tops: number[] = [];
  doc.querySelectorAll(SIDEBAR_TOGGLES.join(',')).forEach((toggle) => {
    toggle.querySelectorAll('*').forEach((part) => {
      const style = view?.getComputedStyle(part);
      if (style && (style.display === 'none' || style.visibility === 'hidden')) return;
      const rect = part.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) tops.push(rect.top);
    });
  });
  return tops.length > 0 ? Math.min(...tops) : null;
}
